import { Fragment, memo } from 'react';
import { flexRender, type Table } from '@tanstack/react-table';
import { TableRow, Typography } from '@mui/material';
import { FooterTableCell, StickyTableFooter } from './TableFooter.styles';

import type { DataSchema, Schema } from 'types';
import { getCommonPinningStyles } from './tableFunctions';
import { useTableManagement } from 'contexts/TableManagementContext.tsx';
import type { TableColumnAggregationState } from 'utils/table';

interface Props<T extends keyof P, P extends DataSchema> {
  tableInstance: Table<Schema<T, P>>;
  hideCheckbox?: boolean;
  /**
   * This prop is needed to trigger a re-render when column aggregation changes.
   * It is not used in the function body, but its presence in the props object ensures that
   * React.memo detects a change and updates the component.
   */
  columnAggregation?: TableColumnAggregationState;
}

const TableFooterBase = <T extends keyof P, P extends DataSchema>({ tableInstance, hideCheckbox }: Props<T, P>) => {
  const { hasInnerTable, shouldShowRowControls, hasHeliosFunctions, hasEditableActions, draggable } = useTableManagement();

  const metaColumnsCount = Number(shouldShowRowControls) + Number(hasHeliosFunctions);

  return (
    <StickyTableFooter>
      {tableInstance.getFooterGroups().map((footerGroup) => (
        <TableRow key={footerGroup.id}>
          {!hideCheckbox && <FooterTableCell sx={{ width: 'calc(var(--col-selection-size) * 1px)' }} />}
          {hasInnerTable && <FooterTableCell sx={{ width: 'calc(var(--col-meta-size) * 1px)' }} />}
          <Fragment>
            {footerGroup.headers.map((header) => {
              if (header.id === 'id' || header.id === 'hidden' || !header.column.getIsVisible()) {
                return null;
              }

              return (
                <FooterTableCell
                  key={header.id}
                  sx={{
                    width: `calc(var(--header-${CSS.escape(header.id)}-size) * 1px)`,
                    ...getCommonPinningStyles(header.column, { type: 'header' }),
                    backgroundColor: 'grey.100',
                  }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                  </Typography>
                </FooterTableCell>
              );
            })}
          </Fragment>
          {metaColumnsCount > 0 && (
            <FooterTableCell
              sx={{
                width: 'auto',
                minWidth: `calc(var(--col-meta-size) * ${metaColumnsCount}px)`,
              }}
            />
          )}
          {draggable && <FooterTableCell sx={{ width: 'calc(var(--col-meta-size) * 1px)' }} />}
          {hasEditableActions && <FooterTableCell sx={{ width: 'calc(var(--col-actions-size) * 1px)' }} />}
        </TableRow>
      ))}
    </StickyTableFooter>
  );
};

export const TableFooter = memo(TableFooterBase) as typeof TableFooterBase;
